/**
 * Casdoor SSO Configuration
 *
 * This file configures the Casdoor SDK for SSO authentication.
 * Environment variables:
 *   - REACT_APP_CASDOOR_ENDPOINT: Casdoor server URL
 *   - REACT_APP_CASDOOR_CLIENT_ID: Application client ID
 *   - REACT_APP_CASDOOR_ORG_NAME: Organization name (optional, has default)
 *   - REACT_APP_CASDOOR_APP_NAME: Application name (optional, has default)
 */
import Sdk from 'casdoor-js-sdk';

// Casdoor server configuration
const CASDOOR_ENDPOINT = 'https://casdoor.freyatech.cn';
const CASDOOR_CLIENT_ID = '08b9feb9af3fbb0d1091';
const CASDOOR_ORG_NAME = '上海丰旗工具有限公司';
const CASDOOR_APP_NAME = '上海丰旗工具有限公司ERP';

// SDK configuration object
const sdkConfig = {
  serverUrl: CASDOOR_ENDPOINT,
  clientId: CASDOOR_CLIENT_ID,
  appName: CASDOOR_APP_NAME,
  organizationName: CASDOOR_ORG_NAME,
  redirectPath: '/callback',
  // Optional: signin path for backend token exchange
  signinPath: '/api/auth/casdoor/signin',
};

// Create SDK instance (lazy initialization to avoid errors when config is missing)
let _sdkInstance = null;

/**
 * Get the Casdoor SDK instance
 * @returns {Sdk|null} SDK instance or null if not configured
 */
export const getCasdoorSDK = () => {
  if (!CASDOOR_CLIENT_ID) {
    console.warn('Casdoor client ID not configured');
    return null;
  }

  if (!_sdkInstance) {
    _sdkInstance = new Sdk(sdkConfig);
  }

  return _sdkInstance;
};

/**
 * Get the Casdoor signin URL
 * @param {string} state - Optional state parameter for CSRF protection
 * @returns {string|null} Signin URL or null if not configured
 */
export const getSigninUrl = (state = '') => {
  const sdk = getCasdoorSDK();
  if (!sdk) return null;

  // Build redirect URI based on current origin
  const redirectUri = `${window.location.origin}/callback`;
  return sdk.getSigninUrl(redirectUri, state);
};

/**
 * Get the Casdoor signup URL
 * @returns {string|null} Signup URL or null if not configured
 */
export const getSignupUrl = () => {
  const sdk = getCasdoorSDK();
  if (!sdk) return null;

  const redirectUri = `${window.location.origin}/callback`;
  return sdk.getSignupUrl(redirectUri);
};

/**
 * Get the Casdoor logout URL
 * @param {string} postLogoutRedirectUri - URL to redirect to after logout
 * @returns {string} Logout URL
 */
export const getLogoutUrl = (postLogoutRedirectUri = window.location.origin) => {
  // Casdoor logout endpoint
  return `${CASDOOR_ENDPOINT}/api/logout?redirectUri=${encodeURIComponent(postLogoutRedirectUri)}`;
};

/**
 * Check if Casdoor SSO is enabled
 * @returns {boolean} True if Casdoor is configured
 */
export const isCasdoorEnabled = () => {
  return Boolean(CASDOOR_CLIENT_ID);
};

/**
 * Parse authorization code from URL
 * @returns {{code: string, state: string}|null} Code and state from URL or null
 */
export const parseAuthCallback = () => {
  const urlParams = new URLSearchParams(window.location.search);
  const code = urlParams.get('code');
  const state = urlParams.get('state');

  if (code) {
    return { code, state };
  }

  return null;
};

/**
 * Check if silent signin is required
 * This is used for SSO across applications
 * @returns {boolean} True if silentSignin=1 is in URL
 */
export const isSilentSigninRequired = () => {
  const urlParams = new URLSearchParams(window.location.search);
  return urlParams.get('silentSignin') === '1';
};

// Export config for reference
export const casdoorConfig = {
  endpoint: CASDOOR_ENDPOINT,
  clientId: CASDOOR_CLIENT_ID,
  orgName: CASDOOR_ORG_NAME,
  appName: CASDOOR_APP_NAME,
};

// Default export for convenience
const casdoorUtils = {
  getCasdoorSDK,
  getSigninUrl,
  getSignupUrl,
  getLogoutUrl,
  isCasdoorEnabled,
  parseAuthCallback,
  isSilentSigninRequired,
  config: casdoorConfig,
};

export default casdoorUtils;
